import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { cn } from "../../lib/cn";
/**
 * A dialog that arrives from an edge.
 *
 * It is Radix's Dialog underneath, not a separate primitive, so it inherits
 * the same focus trap, the same escape handling and the same requirement:
 * every Sheet needs a `SheetTitle`.
 *
 * The mobile navigation drawer in `AppLayout` is the main consumer, which is
 * why `side="left"` gets a narrower default than the others.
 */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
const sheetVariants = cva([
    "lg:fixed lg:z-50 lg:flex lg:flex-col lg:gap-4 lg:bg-card lg:p-6",
    "lg:border-hairline-2 lg:shadow-float lg:outline-none",
    "lg:transition lg:ease-in-out",
    "lg:data-[state=open]:animate-in lg:data-[state=closed]:animate-out",
    "lg:data-[state=closed]:duration-200 lg:data-[state=open]:duration-300",
], {
    variants: {
        side: {
            top: "lg:inset-x-0 lg:top-0 lg:border-b lg:data-[state=closed]:slide-out-to-top lg:data-[state=open]:slide-in-from-top",
            bottom: "lg:inset-x-0 lg:bottom-0 lg:border-t lg:data-[state=closed]:slide-out-to-bottom lg:data-[state=open]:slide-in-from-bottom",
            left: "lg:inset-y-0 lg:left-0 lg:h-full lg:w-72 lg:border-r lg:data-[state=closed]:slide-out-to-left lg:data-[state=open]:slide-in-from-left",
            right: "lg:inset-y-0 lg:right-0 lg:h-full lg:w-96 lg:max-w-full lg:border-l lg:data-[state=closed]:slide-out-to-right lg:data-[state=open]:slide-in-from-right",
        },
    },
    defaultVariants: { side: "right" },
});
export function SheetContent({ className, children, side, ...props }) {
    return (_jsxs(DialogPrimitive.Portal, { children: [_jsx(DialogPrimitive.Overlay, { className: cn("lg:fixed lg:inset-0 lg:z-50 lg:bg-black/70 lg:backdrop-blur-[3px]", "lg:data-[state=open]:animate-in lg:data-[state=closed]:animate-out", "lg:data-[state=closed]:fade-out-0 lg:data-[state=open]:fade-in-0") }), _jsxs(DialogPrimitive.Content, { "data-slot": "sheet-content", className: cn(sheetVariants({ side }), className), ...props, children: [children, _jsxs(DialogPrimitive.Close, { className: cn("lg:absolute lg:right-4 lg:top-4 lg:rounded-sm lg:p-1.5 lg:cursor-pointer", "lg:text-muted-foreground lg:transition-colors lg:hover:text-strong", "lg:outline-none lg:focus-visible:ring-2 lg:focus-visible:ring-ring"), children: [_jsx(X, { className: "lg:size-4", "aria-hidden": "true" }), _jsx("span", { className: "lg:sr-only", children: "Close" })] })] })] }));
}
export function SheetHeader({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sheet-header", className: cn("lg:flex lg:flex-col lg:gap-1.5 lg:pr-8", className), ...props }));
}
export function SheetTitle({ className, ...props }) {
    return (_jsx(DialogPrimitive.Title, { "data-slot": "sheet-title", className: cn("lg:text-h3 lg:font-bold lg:text-strong", className), ...props }));
}
export function SheetDescription({ className, ...props }) {
    return (_jsx(DialogPrimitive.Description, { "data-slot": "sheet-description", className: cn("lg:text-body-sm lg:text-muted-foreground lg:max-w-none", className), ...props }));
}
//# sourceMappingURL=sheet.js.map